import { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitErrorHandler, useForm } from "react-hook-form";

import {
  Button,
  Modal,
  toastError,
  toastSuccess,
  useModal,
} from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetTradingAccount,
  useMutationRequest,
} from "~/v2/api/hooks";
import type { UseCountryReturn } from "~/v2/api/hooks/useCountry";
import type { Owner } from "~/v2/api/rest/account";
import {
  deleteOwner,
  type OwnerType,
  patchOwner,
  postFileUpload,
  postOwner,
} from "~/v2/api/rest/entityOnboarding";
import {
  DEFAULT_AUTHORIZED_INDIVIDUAL_VALUES,
  DEFAULT_UBO_VALUES,
  REQUIRED_ERR_MSG,
} from "../../../constants";
import { useUploadID } from "../../../useUploadID";
import { useUploadOwnerDocument } from "../../../useUploadOwnerDocument";
import {
  getDefaultFormValues,
  isAllAsterisks,
  logAmplitudeEvent,
  type Transform,
  zAffiliationsRefinement,
} from "../../../util";
import { AUTH_INDIVIDUAL_UBO_TRANSFORMS } from "..";
import {
  type AuthorizedIndividual,
  type UltimateBeneficialOwner,
  zAuthorizedIndividual,
  zUltimateBeneficialOwner,
} from "../schema";
import { AuthUBOForm } from "./form";

export type FormOwnerType = Exclude<
  OwnerType,
  "account-opener" | "legal-entity"
>;

type AuthorizedIndividualsUBOsFormProps = {
  owner?: Partial<Owner>;
  isPatch: boolean;
  type: FormOwnerType;
  onClose: () => void;
  percentageOwnershipAvailable?: number;
  country: UseCountryReturn;
  title: string;
  isOpen: boolean;
};

interface DocUpload {
  docType: string;
  docSubType?: string;
  file: File | { id: string; name: string } | null;
  label: string;
}

export const DISPLAY_TYPE: Record<FormOwnerType, string> = {
  "authorized-individual": "Authorized Individual",
  ubo: "Ultimate Beneficial Owner",
};

export const AuthorizedIndividualsUBOsForm = ({
  isPatch,
  owner,
  type,
  onClose,
  percentageOwnershipAvailable = 100,
  country,
  title,
  isOpen,
}: AuthorizedIndividualsUBOsFormProps) => {
  const { account } = useGetTradingAccount();

  const accountId = account?.id ?? "";
  const ownerId = owner?.owner_id ?? "";

  const uploadID = useUploadID({ accountId, ownerId });

  const addressDoc = useUploadOwnerDocument({
    accountId,
    ownerId,
    docType: "address_verification",
  });

  const taxIdDoc = useUploadOwnerDocument({
    accountId,
    ownerId,
    docType: "tax_id_verification",
  });

  const isTaxIDRequired = !owner?.country_of_tax_residence;

  const countryTransform: Transform<Owner, unknown, string | undefined> = {
    keys: ["country_of_citizenship", "country_of_tax_residence"],
    condition: () => true,
    transform: (value) =>
      country.alpha3ToName(typeof value === "string" ? value : ""),
  };

  const form = useForm<UltimateBeneficialOwner | AuthorizedIndividual>({
    mode: "onChange",
    resolver: zodResolver(
      type === "authorized-individual"
        ? zAuthorizedIndividual.superRefine(
            zAffiliationsRefinement({ country, isTaxIDRequired }),
          )
        : zUltimateBeneficialOwner.superRefine(
            zAffiliationsRefinement({
              country,
              percentageOwnershipAvailable,
              isTaxIDRequired,
            }),
          ),
    ),
    defaultValues: getDefaultFormValues(
      owner ?? undefined,
      [
        ...AUTH_INDIVIDUAL_UBO_TRANSFORMS,
        countryTransform,
      ] as Transform<Owner>[],
      type === "authorized-individual"
        ? DEFAULT_AUTHORIZED_INDIVIDUAL_VALUES
        : DEFAULT_UBO_VALUES,
    ),
  });

  const submitMutation = useMutationRequest(
    async (data: UltimateBeneficialOwner | AuthorizedIndividual) => {
      const ownerPayload =
        type === "authorized-individual"
          ? {
              accountId,
              ownerId,
              type: "authorized-individual" as const,
              data: data as AuthorizedIndividual,
            }
          : {
              accountId,
              ownerId,
              type: "ubo" as const,
              data: data as UltimateBeneficialOwner,
            };

      const newOrUpdatedOwner = await (isPatch
        ? patchOwner(ownerPayload)
        : postOwner(ownerPayload));

      if (!newOrUpdatedOwner) {
        throw new Error(
          `Failed to ${owner ? "update" : "create"} ${DISPLAY_TYPE[type]}`,
        );
      }

      const newOwnerId = newOrUpdatedOwner.owner_id ?? "";

      // Build the list of ID uploads: passport uses a single "passport" sub type,
      // other ID types use "front" and "back" sub types.
      const docUploads: DocUpload[] = [];

      if (uploadID.idType === "passport") {
        docUploads.push({
          docType: "identity_verification",
          docSubType: "passport",
          label: "passport",
          file: uploadID.id.front,
        });
      } else {
        docUploads.push({
          docType: "identity_verification",
          docSubType: "front",
          label: "front of ID",
          file: uploadID.id.front,
        });
        docUploads.push({
          docType: "identity_verification",
          docSubType: "back",
          label: "back of ID",
          file: uploadID.id.back,
        });
      }

      if (type === "ubo") {
        docUploads.push({
          docType: "address_verification",
          label: "proof of address",
          file: addressDoc.file,
        });
        docUploads.push({
          docType: "tax_id_verification",
          label: "tax ID document",
          file: taxIdDoc.file,
        });
      }

      await Promise.all(
        docUploads
          .filter(({ file }) => file instanceof File)
          .map(async ({ docType, docSubType, label, file }) => {
            const res = await postFileUpload({
              accountId,
              ownerId: newOwnerId,
              docType,
              docSubType,
              file: file as File,
            });
            if (!res?.id) {
              throw new Error(`Failed to upload ${label}`);
            }
          }),
      );
    },
    {
      onSuccess: () => {
        toastSuccess(`${DISPLAY_TYPE[type]} ${owner ? "updated" : "created"}`);
        onClose();
      },
      onError: (error) => {
        toastError(
          `Error ${isPatch ? "updating" : "creating"} ${DISPLAY_TYPE[type]}: ${getErrorMessage(
            error,
          )}`,
        );
      },
    },
  );

  const deleteMutation = useMutationRequest(
    () => (owner ? deleteOwner({ accountId, ownerId }) : Promise.resolve()),
    {
      onSuccess: () => {
        toastSuccess(`${DISPLAY_TYPE[type]} deleted`);
        onClose();
      },
      onError: (error) => {
        toastError(
          `Error: Failed to delete ${DISPLAY_TYPE[type]}: ${error.message}`,
        );
      },
    },
  );

  const onDelete = async () => {
    deleteMutation.mutate({});
  };

  const onSubmitError: SubmitErrorHandler<
    UltimateBeneficialOwner | AuthorizedIndividual
  > = (errs) => {
    toastError("Form has errors. Please check the form and try again.");
    const errorMessage = Object.values(errs)
      .map((e) => e.message)
      .join(", ");
    logAmplitudeEvent.invalidInput(errorMessage);
  };

  const onSubmit = ({
    country_of_citizenship,
    country_of_tax_residence,
    tax_id,
    ...values
  }: UltimateBeneficialOwner | AuthorizedIndividual) => {
    const isFrontIDRequirementMet = uploadID.imageRequirement.includes("front")
      ? !!uploadID.id.front
      : true;
    const isBackIDRequirementMet = uploadID.imageRequirement.includes("back")
      ? !!uploadID.id.back
      : true;

    if (!isFrontIDRequirementMet) {
      uploadID.setError("front", REQUIRED_ERR_MSG);
    }
    if (!isBackIDRequirementMet) {
      uploadID.setError("back", REQUIRED_ERR_MSG);
    }

    if (!isFrontIDRequirementMet || !isBackIDRequirementMet) {
      toastError("ID image(s) are required");
      return;
    }

    // UBOs must provide proof of address and a tax ID document.
    if (type === "ubo") {
      const isAddressDocMet = !!addressDoc.file;
      const isTaxIdDocMet = !!taxIdDoc.file;

      if (!isAddressDocMet) {
        addressDoc.setError(REQUIRED_ERR_MSG);
      }
      if (!isTaxIdDocMet) {
        taxIdDoc.setError(REQUIRED_ERR_MSG);
      }

      if (!isAddressDocMet || !isTaxIdDocMet) {
        toastError("Proof of address and tax ID document are required");
        return;
      }
    }

    if (
      type === "ubo" &&
      (values as UltimateBeneficialOwner).percentage_ownership >
        percentageOwnershipAvailable
    ) {
      toastError(
        `The sum of each Ultimate Beneficial Owners' ownership percentage cannot exceed 100%. There is ${percentageOwnershipAvailable}% available.`,
      );
      return;
    }

    const countryOfCitizenshipAlpha3 = country.nameToAlpha3(
      country_of_citizenship,
    );
    const countryOfTaxResidenceAlpha3 = country.nameToAlpha3(
      country_of_tax_residence,
    );

    if (!countryOfCitizenshipAlpha3 || !countryOfTaxResidenceAlpha3) {
      toastError(`Please select country from dropdown`);
      return;
    }

    const isTaxIDIncluded = isTaxIDRequired || !isAllAsterisks(tax_id ?? "");

    submitMutation.mutate({
      ...values,
      country_of_citizenship: countryOfCitizenshipAlpha3,
      country_of_tax_residence: countryOfTaxResidenceAlpha3,
      ...(isTaxIDIncluded ? { tax_id } : {}),
    });
  };

  const warningModal = useModal({
    title: "Discard draft?",
    content: "Are you sure you want to discard this draft?",
    size: "sm",
    controls: (
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => {
            warningModal.toggle();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            warningModal.toggle();
            onClose();
          }}
        >
          Yes, I want to leave
        </Button>
      </div>
    ),
  });

  const onWarnBeforeClose = () => {
    warningModal.toggle();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: countryTransform is redefined every render, so listing it would reset the form on every render and discard the user's input.
  useEffect(() => {
    form.reset({
      ...getDefaultFormValues(
        owner ?? undefined,
        [
          ...AUTH_INDIVIDUAL_UBO_TRANSFORMS,
          countryTransform,
        ] as Transform<Owner>[],
        type === "authorized-individual"
          ? DEFAULT_AUTHORIZED_INDIVIDUAL_VALUES
          : DEFAULT_UBO_VALUES,
      ),
      // Only add fake tax id if they are updating an existing owner
      ...(owner?.owner_id && owner?.country_of_tax_residence
        ? { tax_id: "*********" }
        : {}),
    });
  }, [owner, form.reset, type]);

  const isSubmitDisabled = submitMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <Modal
        title={title}
        isOpen={isOpen && !warningModal.isOpen}
        onClose={onWarnBeforeClose}
        size="xl"
        disableOutsideClick
      >
        <AuthUBOForm
          type={type}
          form={form}
          onSubmit={onSubmit}
          isSubmitDisabled={isSubmitDisabled}
          onSubmitError={onSubmitError}
          country={country}
          uploadID={uploadID}
          addressDoc={addressDoc}
          taxIdDoc={taxIdDoc}
          onDelete={isPatch ? onDelete : undefined}
          isTaxIDRequired={isTaxIDRequired}
        />
      </Modal>
      {warningModal.component}
    </>
  );
};
